import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Flag } from "lucide-react";
import { useState } from "react";
import { authClient } from "@/lib/auth-client";

export const Route = createFileRoute("/login")({
	validateSearch: (search: Record<string, unknown>) => ({
		redirect: (search.redirect as string) || undefined,
	}),
	component: LoginPage,
});

function LoginPage() {
	const navigate = useNavigate();
	const { redirect: redirectTo } = Route.useSearch();
	const [isSignUp, setIsSignUp] = useState(false);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [name, setName] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError("");
		setLoading(true);

		try {
			if (isSignUp) {
				const result = await authClient.signUp.email({
					email,
					password,
					name,
				});
				if (result.error) {
					setError(result.error.message || "Sign up failed");
					return;
				}
			} else {
				const result = await authClient.signIn.email({
					email,
					password,
				});
				if (result.error) {
					setError(result.error.message || "Sign in failed");
					return;
				}
			}
			if (redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//")) {
				window.location.href = redirectTo;
			} else {
				navigate({ to: "/" });
			}
		} catch {
			setError("An unexpected error occurred");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="min-h-screen bg-background flex items-center justify-center p-6">
			<div className="w-full max-w-md">
				<div className="flex items-center justify-center gap-3 mb-8">
					<Flag size={32} className="text-cherry" />
					<h1 className="text-4xl text-foreground">
						{isSignUp ? "Create Account" : "Sign In"}
					</h1>
				</div>

				<form
					onSubmit={handleSubmit}
					className="bg-card rounded-xl p-8 space-y-4"
				>
					{error && (
						<div className="bg-destructive/30 border border-destructive text-foreground text-sm rounded-lg p-3">
							{error}
						</div>
					)}

					{isSignUp && (
						<div>
							<label htmlFor="name" className="block text-sm mb-1">
								Name
							</label>
							<input
								id="name"
								type="text"
								value={name}
								onChange={(e) => setName(e.target.value)}
								required
								className="w-full px-4 py-2"
								placeholder="Your name"
							/>
						</div>
					)}

					<div>
						<label htmlFor="email" className="block text-sm mb-1">
							Email
						</label>
						<input
							id="email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							className="w-full px-4 py-2"
							placeholder="you@example.com"
						/>
					</div>

					<div>
						<label htmlFor="password" className="block text-sm mb-1">
							Password
						</label>
						<input
							id="password"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							required
							minLength={8}
							className="w-full px-4 py-2"
							placeholder="Min 8 characters"
						/>
					</div>

					<button
						type="submit"
						disabled={loading}
						className="w-full py-3 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors text-lg uppercase tracking-wider"
					>
						{loading ? "Loading..." : isSignUp ? "Create Account" : "Sign In"}
					</button>

					<p className="text-center text-muted-foreground text-sm">
						{isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
						<button
							type="button"
							onClick={() => {
								setIsSignUp(!isSignUp);
								setError("");
							}}
							className="text-cherry hover:text-cherry/80 font-medium"
						>
							{isSignUp ? "Sign in" : "Sign up"}
						</button>
					</p>
				</form>
			</div>
		</div>
	);
}
